import React, { useState } from 'react';
import { Flame, Crown, Shield, MessageCircle, Heart, Calendar, Sparkles, X, Clock, CreditCard, Check, AlertTriangle, Zap, ExternalLink, Loader, RefreshCw, Users, Star } from 'lucide-react';
import { API_URL } from './apiConfig';

// ── App suite — kept in one place so both walls stay in sync ─────
const APP_SUITE = [
  { name: 'Spark',   icon: <Flame size={16} />,    desc: 'Dating & connections',       color: '#e11d48', active: true },
  { name: 'Connect', icon: <Users size={16} />,     desc: 'Group activities & events',  color: '#14b8a6', active: true },
  { name: 'Maia',    icon: <Star size={16} />,      desc: 'Your personal AI companion', color: '#a78bfa', active: true },
  { name: 'Eros',    icon: <Heart size={16} />,     desc: 'Couples relationship tools', color: '#be185d', active: true },
  { name: 'Artemis', icon: <Shield size={16} />,    desc: 'Babysitting marketplace',    color: '#f59e0b', active: true },
  { name: 'More apps coming', icon: <Sparkles size={16} />, desc: 'New apps added regularly', color: '#94a3b8', active: false },
];

const FEATURES = [
  { icon: <Heart size={18} />,       text: 'Unlimited swipes & likes' },
  { icon: <MessageCircle size={18} />, text: 'Unlimited messaging across all apps' },
  { icon: <Calendar size={18} />,    text: 'AI-powered date & activity booking' },
  { icon: <Shield size={18} />,      text: 'Identity verified community' },
  { icon: <Star size={18} />,        text: 'Maia — your personal AI companion' },
  { icon: <Zap size={18} />,         text: 'Every future Narbodica app included' },
];

export default function SubscriptionPaywall({ subscription, user, onSubscriptionUpdate, onClose }) {
  const [loading, setLoading]               = useState(false);
  const [showCancelConfirm, setShowCancel]  = useState(false);
  const [cancelReason, setCancelReason]     = useState('');
  const [message, setMessage]               = useState('');
  const [error, setError]                   = useState('');

  const status       = subscription?.status;
  const isValid      = subscription?.isValid;
  const isTrial      = status === 'trial';
  const isActive     = status === 'active';
  const isCancelled  = status === 'cancelled';
  const isPastDue    = status === 'past_due';
  const isExpired    = status === 'expired';
  const trialUsed    = subscription?.trialUsed;
  const inGrace      = subscription?.inGracePeriod;
  const daysRemaining = subscription?.daysRemaining || 0;
  const hasBeenMember = trialUsed || isActive || isCancelled || isPastDue || (isExpired && subscription?.currentPeriodEnd);

  // ── API helpers ────────────────────────────────────────────────
  const getToken = () => localStorage.getItem('accessToken');

  const refreshSub = async () => {
    try {
      const r = await fetch(`${API_URL}/subscription/status`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const d = await r.json();
      if (d.success && onSubscriptionUpdate) onSubscriptionUpdate(d.data.subscription);
    } catch {}
  };

  const handleSubscribe = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/subscription/create-checkout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }
      });
      const d = await r.json();
      if (d.success && d.data.checkoutUrl) window.location.href = d.data.checkoutUrl;
      else setError(d.message || 'Failed to start checkout. Please try again.');
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  const handleManageBilling = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/subscription/billing-portal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' }
      });
      const d = await r.json();
      if (d.success && d.data.portalUrl) window.location.href = d.data.portalUrl;
      else setError(d.message || 'Failed to open billing portal.');
    } catch { setError('Network error. Please try again.'); }
    setLoading(false);
  };

  const handleCancel = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/subscription/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason })
      });
      const d = await r.json();
      if (d.success) { setMessage(d.message); setShowCancel(false); refreshSub(); }
      else setError(d.message || 'Failed to cancel.');
    } catch { setError('Failed to cancel. Please try again.'); }
    setLoading(false);
  };

  // ── Shared buttons ──────────────────────────────────────────────
  const SubscribeBtn = ({ label, compact }) => (
    <button onClick={handleSubscribe} disabled={loading}
      style={{ width: '100%', padding: compact ? '15px' : '17px', border: 'none', borderRadius: compact ? '14px' : '16px',
        cursor: loading ? 'default' : 'pointer', fontSize: compact ? '15px' : '16px', fontWeight: 700,
        background: loading ? '#94a3b8' : 'linear-gradient(135deg, #e11d48, #be123c)', color: 'white',
        boxShadow: loading ? 'none' : '0 4px 20px rgba(225,29,72,0.35)', transition: 'transform 0.2s, box-shadow 0.2s',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
      onMouseOver={e => { if (!loading) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 8px 28px rgba(225,29,72,0.4)'; }}}
      onMouseOut={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow=loading?'none':'0 4px 20px rgba(225,29,72,0.35)'; }}
    >
      {loading ? <><Loader size={18} style={{animation:'spin 1s linear infinite'}} /> Processing...</>
               : <>{label || 'Subscribe — $25/month'}</>}
    </button>
  );

  const BillingBtn = () => (
    <button onClick={handleManageBilling} disabled={loading}
      style={{ width: '100%', padding: '14px', border: '1px solid #e5e7eb', borderRadius: '12px',
        cursor: 'pointer', fontSize: '14px', fontWeight: 600, background: 'var(--surface,#fff)',
        color: 'var(--text-primary,#333)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
      <ExternalLink size={16} />
      {loading ? 'Opening...' : 'Manage Billing & Payment'}
    </button>
  );

  // ── WALL: ACTIVE SUBSCRIPTION (management view) ─────────────────
  if (isValid && !isExpired) {
    return (
      <div style={{ maxWidth: '440px', margin: '0 auto', background: 'var(--card-bg,#fff)', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', minHeight: '60vh' }}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1a1a2e,#16213e,#0f3460)', padding: '28px 24px', color: 'white', position: 'relative' }}>
          {onClose && (
            <button onClick={onClose} style={{ position:'absolute', top:'16px', right:'16px', background:'rgba(255,255,255,0.15)', border:'none', color:'white', borderRadius:'8px', padding:'6px', cursor:'pointer' }}>
              <X size={16} />
            </button>
          )}
          <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'8px' }}>
            <div style={{ background: isTrial ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'linear-gradient(135deg,#10b981,#059669)', borderRadius:'12px', padding:'10px', display:'flex' }}>
              {isTrial ? <Clock size={22} /> : <Crown size={22} />}
            </div>
            <div>
              <h2 style={{ margin:0, fontSize:'20px', fontWeight:600 }}>
                {isTrial ? 'Free Trial' : isCancelled ? 'Subscription Ending' : isPastDue ? 'Payment Issue' : 'Narbodica Member'}
              </h2>
              <p style={{ margin:0, fontSize:'13px', opacity:0.8 }}>
                {isTrial ? `${daysRemaining} day${daysRemaining!==1?'s':''} remaining`
                  : isCancelled ? `Access until ${new Date(subscription.currentPeriodEnd).toLocaleDateString('en-AU')}`
                  : isPastDue ? 'Please update your payment method'
                  : `$25/month · Renews ${new Date(subscription.currentPeriodEnd).toLocaleDateString('en-AU')}`}
              </p>
            </div>
          </div>
          {isTrial && (
            <div style={{ marginTop:'16px' }}>
              <div style={{ background:'rgba(255,255,255,0.15)', borderRadius:'8px', height:'6px', overflow:'hidden' }}>
                <div style={{ background: daysRemaining<=2 ? 'linear-gradient(90deg,#ef4444,#dc2626)' : 'linear-gradient(90deg,#f59e0b,#fbbf24)', height:'100%', borderRadius:'8px', width:`${Math.max(5,(daysRemaining/7)*100)}%` }} />
              </div>
              <p style={{ margin:'8px 0 0', fontSize:'12px', opacity:0.7 }}>
                {daysRemaining<=2 ? '⚠️ Trial ending soon — subscribe to keep access' : 'Full access to all features during trial'}
              </p>
            </div>
          )}
        </div>

        {/* App list */}
        <div style={{ padding:'20px 24px' }}>
          <h3 style={{ margin:'0 0 12px', fontSize:'13px', fontWeight:700, color:'var(--text-primary,#333)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Your Narbodica Apps</h3>
          {APP_SUITE.filter(a => a.active).map((app,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px', borderRadius:'10px', background:'var(--bg-secondary,#f8f9fa)', marginBottom:'6px' }}>
              <div style={{ background:app.color, borderRadius:'8px', padding:'6px', color:'white', display:'flex' }}>{app.icon}</div>
              <div style={{ flex:1 }}>
                <p style={{ margin:0, fontWeight:600, fontSize:'13px', color:'var(--text-primary,#333)' }}>{app.name}</p>
                <p style={{ margin:0, fontSize:'12px', color:'var(--text-secondary,#666)' }}>{app.desc}</p>
              </div>
              <Check size={16} style={{ color:'#10b981' }} />
            </div>
          ))}
        </div>

        {isTrial && <div style={{ padding:'0 24px 20px' }}><SubscribeBtn compact /><p style={{ textAlign:'center', fontSize:'12px', color:'var(--text-secondary,#888)', margin:'8px 0 0' }}>One subscription covers all Narbodica apps</p></div>}
        {isPastDue && <div style={{ padding:'0 24px 16px' }}><div style={{ padding:'14px', borderRadius:'12px', background:'#fef3c7', border:'1px solid #fbbf24', display:'flex', alignItems:'center', gap:'10px' }}><AlertTriangle size={18} style={{color:'#d97706'}}/><p style={{ margin:0, fontSize:'13px', color:'#92400e' }}>Your last payment failed. Please update your payment details.</p></div></div>}
        {(isActive||isCancelled||isPastDue) && subscription?.paymentProvider==='stripe' && <div style={{ padding:'0 24px 16px' }}><BillingBtn /></div>}
        {isActive && !showCancelConfirm && <div style={{ padding:'0 24px 20px' }}><button onClick={()=>setShowCancel(true)} style={{ width:'100%', padding:'12px', border:'1px solid #e5e7eb', borderRadius:'12px', cursor:'pointer', fontSize:'14px', background:'transparent', color:'var(--text-secondary,#888)' }}>Cancel Subscription</button></div>}
        {isCancelled && !showCancelConfirm && <div style={{ padding:'0 24px 20px' }}><div style={{ padding:'14px', borderRadius:'12px', background:'#fef3c7', border:'1px solid #fbbf24', display:'flex', alignItems:'center', gap:'10px' }}><AlertTriangle size={18} style={{color:'#d97706'}}/><p style={{ margin:0, fontSize:'13px', color:'#92400e' }}>Cancelled. Access continues until {new Date(subscription.currentPeriodEnd).toLocaleDateString('en-AU')}.</p></div></div>}

        {showCancelConfirm && (
          <div style={{ padding:'0 24px 20px' }}>
            <div style={{ padding:'16px', borderRadius:'12px', background:'#fef2f2', border:'1px solid #fecaca' }}>
              <p style={{ margin:'0 0 12px', fontWeight:600, color:'#991b1b', fontSize:'14px' }}>Cancel subscription?</p>
              <p style={{ margin:'0 0 12px', fontSize:'13px', color:'#b91c1c' }}>You'll keep access until the end of your billing period.</p>
              <textarea value={cancelReason} onChange={e=>setCancelReason(e.target.value)} placeholder="Optional: tell us why..." style={{ width:'100%', padding:'10px', border:'1px solid #fecaca', borderRadius:'8px', fontSize:'13px', minHeight:'60px', resize:'vertical', marginBottom:'12px', boxSizing:'border-box' }} />
              <div style={{ display:'flex', gap:'8px' }}>
                <button onClick={()=>setShowCancel(false)} style={{ flex:1, padding:'10px', border:'1px solid #e5e7eb', borderRadius:'8px', cursor:'pointer', fontSize:'13px', background:'white', color:'#333' }}>Keep Subscription</button>
                <button onClick={handleCancel} disabled={loading} style={{ flex:1, padding:'10px', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', background:'#dc2626', color:'white', opacity:loading?0.6:1 }}>{loading?'Cancelling...':'Confirm Cancel'}</button>
              </div>
            </div>
          </div>
        )}

        {message && <div style={{padding:'0 24px 20px'}}><div style={{padding:'12px 16px',borderRadius:'10px',background:'#f0fdf4',border:'1px solid #bbf7d0',fontSize:'13px',color:'#166534'}}>{message}</div></div>}
        {error   && <div style={{padding:'0 24px 20px'}}><div style={{padding:'12px 16px',borderRadius:'10px',background:'#fef2f2',border:'1px solid #fecaca',fontSize:'13px',color:'#991b1b'}}>{error}</div></div>}
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── WALL 2: ACCESS ON HOLD (returning user — past_due / expired / cancelled post-period) ──
  if (hasBeenMember) {
    // Context-specific messaging
    let headline, subline, detail, cta, showBilling;

    if (isPastDue || inGrace) {
      // Payment failed — most urgent
      headline    = 'Payment didn\'t go through';
      subline     = 'Your access is on hold while we sort this out.';
      detail      = 'Your last payment failed. It happens — just update your payment details and you\'ll be straight back in.';
      cta         = 'Update Payment Details';
      showBilling = true;
    } else if (isExpired && subscription?.paymentProvider === 'stripe') {
      // Had a paid sub that lapsed
      headline    = 'Good to have you back';
      subline     = 'Your subscription lapsed — everything\'s still here waiting.';
      detail      = 'Your account and all your data are safe. Subscribe to pick up right where you left off.';
      cta         = 'Resubscribe — $25/month';
      showBilling = false;
    } else if (isCancelled) {
      // Cancelled and period has now ended
      headline    = 'Ready when you are';
      subline     = 'Your subscription ended — come back any time.';
      detail      = 'Your profile and history are all here. Jump back in whenever you\'re ready.';
      cta         = 'Resubscribe — $25/month';
      showBilling = false;
    } else {
      // Trial ended
      headline    = 'Your free trial ended';
      subline     = 'Hope you got a feel for what Narbodica can do.';
      detail      = 'Subscribe to keep going — one subscription covers every app we build.';
      cta         = 'Subscribe — $25/month';
      showBilling = false;
    }

    return (
      <div style={{ maxWidth: '400px', margin: '0 auto', background: 'var(--card-bg,#fff)', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.1)' }}>
        {/* Minimal header */}
        <div style={{ background: 'linear-gradient(135deg,#1a1a2e,#16213e)', padding: '32px 24px 24px', color: 'white', textAlign: 'center' }}>
          <div style={{ background: isPastDue ? 'linear-gradient(135deg,#f97316,#ea580c)' : 'linear-gradient(135deg,#7c3aed,#6d28d9)', borderRadius: '16px', padding: '14px', display: 'inline-flex', marginBottom: '14px' }}>
            {isPastDue ? <CreditCard size={28} /> : <RefreshCw size={28} />}
          </div>
          <h2 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: 700, fontFamily: '"DM Serif Display",serif' }}>{headline}</h2>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.75, lineHeight: 1.5 }}>{subline}</p>
        </div>

        {/* Detail message */}
        <div style={{ padding: '24px 24px 8px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary,#555)', lineHeight: 1.7, textAlign: 'center' }}>{detail}</p>
        </div>

        {/* Pricing reminder */}
        <div style={{ padding: '16px 24px' }}>
          <div style={{ background: 'var(--bg-secondary,#f8f9fa)', borderRadius: '12px', padding: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '15px', color: 'var(--text-primary,#333)' }}>Narbodica subscription</p>
              <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-secondary,#666)' }}>All apps · Cancel any time · No lock-in</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '17px', color: '#e11d48' }}>$25</p>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary,#888)' }}>AUD/mo</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div style={{ padding: '0 24px 12px' }}>
          {showBilling
            ? <BillingBtn />
            : <SubscribeBtn label={cta} />
          }
        </div>

        {/* Refresh check */}
        <div style={{ padding: '0 24px 20px', textAlign: 'center' }}>
          <button onClick={refreshSub} style={{ background: 'none', border: 'none', color: 'var(--text-secondary,#999)', fontSize: '13px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <RefreshCw size={13} /> Already sorted? Check again
          </button>
        </div>

        {error && <div style={{padding:'0 24px 16px'}}><div style={{padding:'12px 16px',borderRadius:'10px',background:'#fef2f2',border:'1px solid #fecaca',fontSize:'13px',color:'#991b1b',textAlign:'center'}}>{error}</div></div>}
        <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  // ── WALL 1: NEW USER / UPSELL ────────────────────────────────────
  return (
    <div style={{ maxWidth: '440px', margin: '0 auto', background: 'var(--card-bg,#fff)', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', minHeight: '90vh' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#16213e 40%,#0f3460 70%,#1a1a2e 100%)', padding: '40px 24px 32px', color: 'white', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position:'absolute', top:'-40px', right:'-40px', width:'160px', height:'160px', background:'radial-gradient(circle, rgba(225,29,72,0.15) 0%, transparent 70%)', borderRadius:'50%' }} />
        <div style={{ position:'absolute', bottom:'-20px', left:'-30px', width:'120px', height:'120px', background:'radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)', borderRadius:'50%' }} />
        <div style={{ background:'linear-gradient(135deg,#e11d48,#be123c)', borderRadius:'20px', padding:'16px', display:'inline-flex', marginBottom:'16px', boxShadow:'0 4px 20px rgba(225,29,72,0.35)' }}>
          <Crown size={32} />
        </div>
        <h1 style={{ margin:'0 0 8px', fontSize:'26px', fontWeight:700, fontFamily:'"DM Serif Display",serif' }}>Narbodica</h1>
        <p style={{ margin:0, fontSize:'15px', opacity:0.85, lineHeight:1.5 }}>Subscribe to unlock all features</p>
        <div style={{ marginTop:'16px', display:'inline-flex', alignItems:'center', gap:'4px', background:'rgba(255,255,255,0.1)', borderRadius:'8px', padding:'8px 16px', fontSize:'13px' }}>
          <CreditCard size={14} />
          <span>One subscription · All apps · $25/month</span>
        </div>
      </div>

      {/* Features */}
      <div style={{ padding:'24px' }}>
        <h3 style={{ margin:'0 0 16px', fontSize:'16px', fontWeight:700, color:'var(--text-primary,#333)' }}>Everything you get</h3>
        {FEATURES.map((f,i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:'14px', padding:'12px 0', borderBottom: i<FEATURES.length-1?'1px solid var(--border-color,#f0f0f0)':'none' }}>
            <div style={{ background:'linear-gradient(135deg,#fecdd3,#ffe4e6)', borderRadius:'10px', padding:'8px', display:'flex', color:'#e11d48' }}>{f.icon}</div>
            <p style={{ margin:0, fontSize:'14px', fontWeight:500, color:'var(--text-primary,#333)' }}>{f.text}</p>
          </div>
        ))}
      </div>

      {/* App suite */}
      <div style={{ padding:'0 24px 24px' }}>
        <div style={{ background:'linear-gradient(135deg,#faf5ff,#f5f3ff)', borderRadius:'16px', padding:'16px', border:'1px solid #e9d5ff' }}>
          <p style={{ margin:'0 0 6px', fontSize:'12px', fontWeight:700, color:'#7c3aed', textTransform:'uppercase', letterSpacing:'0.5px' }}>Narbodica App Suite</p>
          <p style={{ margin:'0 0 12px', fontSize:'13px', color:'#6b21a8', lineHeight:1.5 }}>$25/month covers every app — current and future. No extra charges, ever.</p>
          {APP_SUITE.map((app,i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'7px 0', opacity:app.active?1:0.5 }}>
              <div style={{ background:app.active?app.color:'#94a3b8', borderRadius:'7px', padding:'5px', color:'white', display:'flex' }}>{app.icon}</div>
              <span style={{ fontSize:'13px', fontWeight:500, color:'#4c1d95' }}>{app.name}{!app.active?' (Coming Soon)':''}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Subscribe */}
      <div style={{ padding:'0 24px 12px' }}><SubscribeBtn /></div>
      <div style={{ padding:'0 24px 24px', textAlign:'center' }}>
        <p style={{ margin:0, fontSize:'11px', color:'var(--text-secondary,#999)', lineHeight:1.6 }}>
          Cancel anytime. No lock-in contracts.<br />
          Prices in AUD. Powered by Stripe. Subscription auto-renews monthly.
        </p>
      </div>

      {error   && <div style={{padding:'0 24px 24px'}}><div style={{padding:'14px 16px',borderRadius:'12px',background:'#fef2f2',border:'1px solid #fecaca',fontSize:'13px',color:'#991b1b',textAlign:'center'}}>{error}</div></div>}
      {message && <div style={{padding:'0 24px 24px'}}><div style={{padding:'14px 16px',borderRadius:'12px',background:'#eff6ff',border:'1px solid #bfdbfe',fontSize:'13px',color:'#1e40af',textAlign:'center'}}>{message}</div></div>}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
